import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { getUserScope, hasScopeRestriction, vigilanteVisible, scopeSignature } from './roleScope';
import {
  fetchTiendasPorInspector,
  fetchClientesPorInspector,
  fetchTiendasPorDelegacion,
  fetchClientesPorDelegacion,
  fetchVigilantesPorDelegacion,
} from '../../components/admin/firestoreService';

const dedupById = (arr) => {
  const map = new Map();
  for (const item of arr) {
    if (item && item.id) map.set(item.id, item);
  }
  return Array.from(map.values());
};

/**
 * Devuelve los clientes visibles para el usuario actual, según su scope.
 *  - administrador → todos los clientes con borrado != true.
 *  - inspector → derivados de tiendas con inspectorCod.
 *  - administrativo → derivados de tiendas con delegacion ∈ delegaciones.
 *  - combinación → unión deduplicada.
 *  - rol sin scope efectivo → [].
 */
export async function loadVisibleClientes(adminData, currentService) {
  if (!currentService?.id) return [];
  const scope = getUserScope(adminData);

  if (scope.isAll) {
    const q = query(
      collection(db, 'servicios', currentService.id, 'clientes'),
      where('borrado', '==', false)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  if (!hasScopeRestriction(scope)) return [];

  const promises = [];
  if (scope.isInspector && scope.inspectorCods.length > 0) {
    scope.inspectorCods.forEach((cod) =>
      promises.push(fetchClientesPorInspector(cod, currentService))
    );
  }
  // Las delegaciones solo amplían el scope de clientes para administrativos.
  // Para inspector, las delegaciones solo aplican a vigilantes (sus clientes
  // se derivan de inspectorCod). Ver roleScope / role-scope-model.
  // AND por empresa: si el administrativo tiene empresas asignadas, los clientes
  // se derivan solo de sus tiendas en esas empresas.
  if (scope.isAdministrativo && scope.delegaciones.length > 0) {
    promises.push(fetchClientesPorDelegacion(scope.delegaciones, currentService, scope.empresas));
  }
  const lotes = await Promise.all(promises);
  return dedupById(lotes.flat().filter((c) => c.borrado !== true));
}

/**
 * Devuelve las tiendas visibles para el usuario actual, según su scope.
 *  - administrador → todas las tiendas con borrado != true.
 *  - inspector → fetchTiendasPorInspector.
 *  - administrativo → fetchTiendasPorDelegacion.
 *  - combinación → unión deduplicada.
 *  - rol sin scope efectivo → [].
 */
export async function loadVisibleTiendas(adminData, currentService) {
  if (!currentService?.id) return [];
  const scope = getUserScope(adminData);

  if (scope.isAll) {
    const q = query(
      collection(db, 'servicios', currentService.id, 'tiendas'),
      where('borrado', '==', false)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  if (!hasScopeRestriction(scope)) return [];

  const promises = [];
  if (scope.isInspector && scope.inspectorCods.length > 0) {
    scope.inspectorCods.forEach((cod) =>
      promises.push(fetchTiendasPorInspector(cod, currentService))
    );
  }
  // Las delegaciones solo amplían el scope de tiendas para administrativos.
  // Para inspector, las delegaciones solo aplican a vigilantes (sus tiendas
  // se derivan de inspectorCod). Ver roleScope / role-scope-model.
  // AND por empresa: si el administrativo tiene empresas asignadas, solo ve las
  // tiendas de esas empresas dentro de sus delegaciones.
  if (scope.isAdministrativo && scope.delegaciones.length > 0) {
    promises.push(fetchTiendasPorDelegacion(scope.delegaciones, currentService, scope.empresas));
  }
  const lotes = await Promise.all(promises);
  return dedupById(lotes.flat().filter((t) => t.borrado !== true));
}

// Cache en memoria de las tiendas activas visibles. El resultado no depende
// del mes seleccionado, pero los selectores de cuadrantes lo recargaban en
// cada cambio de mes / apertura del autocomplete (~1.6k docs por long-polling
// cada vez). Promesa compartida con TTL corto: las cargas concurrentes y las
// recargas dentro de la ventana reutilizan una única lectura de Firestore.
const tiendasActivasCache = new Map();
const TIENDAS_ACTIVAS_TTL_MS = 5 * 60 * 1000;

/** Invalida la caché de tiendas activas (p. ej. tras editar tiendas). */
export function invalidarTiendasActivasCache() {
  tiendasActivasCache.clear();
}

async function fetchVisibleTiendasActivas(adminData, currentService) {
  const scope = getUserScope(adminData);

  if (scope.isAll) {
    // Solo equality sobre activoFreematica → no requiere índice compuesto.
    // `borrado` se filtra en memoria (mismo patrón que fetchTiendasPorInspector).
    const q = query(
      collection(db, 'servicios', currentService.id, 'tiendas'),
      where('activoFreematica', '==', true)
    );
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((t) => t.borrado !== true);
  }

  // Inspector/administrativo: loadVisibleTiendas ya devuelve solo activas.
  return loadVisibleTiendas(adminData, currentService);
}

/**
 * Igual que `loadVisibleTiendas`, pero devolviendo SOLO las tiendas activas en
 * Freematica (`activoFreematica == true`). Para el rol administrador filtra
 * server-side (`where('activoFreematica','==',true)`) en lugar de descargar
 * todas las tiendas (~17k) y filtrar en memoria — evita el cuelgue de la carga
 * inicial. Para inspector/administrativo reutiliza los fetchers existentes, que
 * ya filtran `activoFreematica==true` server-side.
 *
 * Pensado para selectores de servicios (p. ej. cuadrantes), donde solo tienen
 * sentido los servicios vigentes. Resultado cacheado en memoria (TTL 5 min).
 */
export async function loadVisibleTiendasActivas(adminData, currentService) {
  if (!currentService?.id) return [];

  // La firma del scope va en la clave: si un admin cambia las delegaciones/
  // empresas/inspector del usuario, la clave cambia y se relee al instante
  // (no se sirve el scope anterior durante el TTL).
  const key = `${currentService.id}|${adminData?.id || adminData?.email || 'anon'}|${scopeSignature(adminData)}`;
  const cached = tiendasActivasCache.get(key);
  if (cached && Date.now() - cached.ts < TIENDAS_ACTIVAS_TTL_MS) return cached.promise;

  const promise = fetchVisibleTiendasActivas(adminData, currentService);
  tiendasActivasCache.set(key, { ts: Date.now(), promise });
  // Si la descarga falla, no dejamos la promesa rechazada cacheada.
  promise.catch(() => tiendasActivasCache.delete(key));
  return promise;
}

/**
 * Clave única de servicio: `codEmpresa|delegacion|numTienda`.
 *
 * IMPORTANTE: `numTienda` NO es único por sí solo (nombres genéricos como
 * "SERVICIO ORDINARIO" se repiten en muchas empresas/delegaciones). Para
 * desambiguar hay que combinarlo con la empresa y la delegación.
 */
export const serviceScopeKey = (empresa, delegacion, numTienda) => {
  if (empresa == null || delegacion == null || numTienda == null) return null;
  return `${String(empresa).trim()}|${String(delegacion).trim()}|${String(numTienda).trim()}`;
};

/**
 * Extrae empresa y delegación de un `vigilanteId` Freematica con formato
 * `EMP__DELEG__PERSONA` (p. ej. `0020__06__22784`). Devuelve null si el id no
 * sigue ese formato (vigilantes legacy/manuales).
 */
export const parseVigilanteScope = (vigilanteId) => {
  const parts = String(vigilanteId || '').split('__');
  if (parts.length < 3 || !parts[0] || !parts[1]) return null;
  return { empresa: parts[0], delegacion: parts[1] };
};

/**
 * Devuelve un matcher de servicios visibles para filtrar colecciones que
 * referencian servicios (p. ej. `registrosJornada.vigilanteTiendas[]`).
 *
 * El identificador ÚNICO y preferente de un servicio es el id del documento de
 * tienda (`tiendaId`), que apunta unívocamente a su localizador Freematica
 * (contrato/servicio). Como los registros históricos solo guardan el nombre
 * (`numero`), se mantiene un criterio de respaldo por clave compuesta
 * `codEmpresa|delegacion|numTienda` (la empresa/delegación se derivan del
 * `vigilanteId`). Usar `serviceEntryVisible` para evaluar cada entrada.
 *
 *  - administrador → `{ isAll: true }` (sin restricción).
 *  - inspector/administrativo → ids + claves de sus tiendas visibles.
 *  - rol sin scope efectivo → conjuntos vacíos (no ve nada).
 *
 * Se apoya en `loadVisibleTiendas` (consultas cacheadas), así que es barato.
 *
 * @returns {Promise<{isAll: boolean, ids: Set<string>, keys: Set<string>}>}
 */
export async function loadVisibleServiceMatcher(adminData, currentService) {
  const scope = getUserScope(adminData);
  if (scope.isAll) return { isAll: true, ids: null, keys: null };

  const tiendas = await loadVisibleTiendas(adminData, currentService);
  const ids = new Set();
  const keys = new Set();
  for (const t of tiendas) {
    if (t.id != null) ids.add(String(t.id));
    const key = serviceScopeKey(t.codEmpresa, t.delegacion, t.numTienda);
    if (key) keys.add(key);
  }
  return { isAll: false, ids, keys };
}

/**
 * ¿Una entrada de `vigilanteTiendas` está dentro del scope?
 * Prioriza el id único de tienda (`tiendaId`); si la entrada no lo trae
 * (registros antiguos), recurre a la clave compuesta con la empresa/delegación
 * del vigilante (`vigScope`, salida de `parseVigilanteScope`).
 */
export function serviceEntryVisible(entry, matcher, vigScope) {
  if (!matcher || matcher.isAll) return true;
  if (!entry) return false;

  if (entry.tiendaId != null && matcher.ids.has(String(entry.tiendaId))) {
    return true;
  }
  if (vigScope && entry.numero != null) {
    const key = serviceScopeKey(vigScope.empresa, vigScope.delegacion, entry.numero);
    if (key && matcher.keys.has(key)) return true;
  }
  return false;
}

/**
 * Devuelve {clientes, tiendas} en una sola llamada.
 */
export async function loadVisibleClientesYTiendas(adminData, currentService) {
  const [clientes, tiendas] = await Promise.all([
    loadVisibleClientes(adminData, currentService),
    loadVisibleTiendas(adminData, currentService),
  ]);
  return { clientes, tiendas };
}

/**
 * Devuelve los VIGILANTES visibles para el usuario actual.
 *  - administrador → todos los vigilantes no borrados.
 *  - inspector/administrativo → por delegaciones, AND empresa si aplica.
 *  - rol sin scope efectivo → [].
 */
export async function loadVisibleVigilantes(adminData, currentService) {
  if (!currentService?.id) return [];
  const scope = getUserScope(adminData);

  if (scope.isAll) {
    const snap = await getDocs(collection(db, 'servicios', currentService.id, 'vigilantes'));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((v) => v.borrado !== true);
  }

  if (!hasScopeRestriction(scope)) return [];

  const vigilantes = scope.delegaciones.length > 0
    ? await fetchVigilantesPorDelegacion(scope.delegaciones, currentService)
    : [];
  return vigilantes.filter((v) => vigilanteVisible(v, scope));
}

/**
 * Devuelve un matcher de scope aplicable a la colección `documents`.
 * Los documentos guardan `tienda` como `numTienda` (o, en flujos antiguos,
 * como id de tienda) y `cliente` como `nombreCliente`. Para no depender de
 * la convención del callsite, el matcher acumula numTienda, id de tienda y
 * nombreCliente de las tiendas/clientes visibles.
 *
 * - administrador → { isAll: true, tiendas: null, clientes: null }
 * - sin scope efectivo → conjuntos vacíos (no verá nada).
 */
export async function loadVisibleDocumentMatchers(adminData, currentService) {
  const scope = getUserScope(adminData);
  if (scope.isAll) {
    return { isAll: true, tiendas: null, clientes: null };
  }
  if (!hasScopeRestriction(scope)) {
    return { isAll: false, tiendas: new Set(), clientes: new Set() };
  }
  const [clientes, tiendas] = await Promise.all([
    loadVisibleClientes(adminData, currentService),
    loadVisibleTiendas(adminData, currentService),
  ]);
  const tiendasSet = new Set();
  for (const t of tiendas) {
    if (t?.id != null) tiendasSet.add(String(t.id));
    if (t?.numTienda != null) tiendasSet.add(String(t.numTienda));
  }
  const clientesSet = new Set();
  for (const c of clientes) {
    if (c?.id != null) clientesSet.add(String(c.id));
    if (c?.nombre != null) clientesSet.add(String(c.nombre));
    if (c?.nombreCliente != null) clientesSet.add(String(c.nombreCliente));
  }
  return { isAll: false, tiendas: tiendasSet, clientes: clientesSet };
}

/**
 * Inicio del mes anterior. Es el límite inferior más antiguo que necesita el
 * dashboard (KPI "documentos mes anterior"); donut (30 días) y gráfico de
 * actividad (14 días) caen siempre dentro de esta ventana, así que una sola
 * descarga sirve a los cuatro widgets.
 */
function startOfPrevMonth(d = new Date()) {
  const x = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  x.setHours(0, 0, 0, 0);
  return x;
}

/**
 * Expande un valor a las formas string y numérica para que el operador `in`
 * coja documentos cuyo `tienda`/`cliente` esté guardado como número o como
 * cadena (el filtrado client-side antiguo hacía String(d.tienda) y casaba
 * ambos; server-side el tipo debe coincidir).
 */
function expandMatchValue(v, out) {
  if (v == null) return;
  const s = String(v);
  if (!s) return;
  out.add(s);
  if (/^\d+$/.test(s)) out.add(Number(s));
}

// Dedup de la descarga de documentos con scope para el KPI de documentos del
// dashboard. Promesa compartida con TTL corto: montajes simultáneos reutilizan
// una única lectura de Firestore.
const scopedDocsCache = new Map();
const SCOPED_DOCS_TTL_MS = 2 * 60 * 1000;
const SCOPED_IN_CHUNK = 30; // límite del operador `in` en Firestore

async function fetchVisibleDocsSince(serviceId, since, tiendaValues, clienteValues) {
  const ref = collection(db, 'servicios', serviceId, 'documents');
  const sinceTs = Timestamp.fromDate(since);
  const byId = new Map();

  // Todas las consultas (chunks de tienda y de cliente) se lanzan en paralelo:
  // en frío, encadenarlas en serie multiplicaba la latencia por el nº de chunks.
  const queries = [];
  const addQueries = (field, values) => {
    for (let i = 0; i < values.length; i += SCOPED_IN_CHUNK) {
      const chunk = values.slice(i, i + SCOPED_IN_CHUNK);
      queries.push(getDocs(query(
        ref,
        where(field, 'in', chunk),
        where('fecha', '>=', sinceTs),
      )));
    }
  };
  addQueries('tienda', tiendaValues);
  addQueries('cliente', clienteValues);

  // tienda y cliente pueden casar el mismo documento → dedup por id evita
  // doble conteo en los KPIs.
  const snaps = await Promise.all(queries);
  snaps.forEach((snap) => snap.forEach((d) => {
    if (!byId.has(d.id)) byId.set(d.id, { id: d.id, ...d.data() });
  }));

  const arr = Array.from(byId.values());
  arr.sort((a, b) => {
    const am = a.fecha?.toMillis ? a.fecha.toMillis() : 0;
    const bm = b.fecha?.toMillis ? b.fecha.toMillis() : 0;
    return bm - am;
  });
  return arr;
}

/**
 * Descarga, UNA sola vez por servicio+usuario, los documentos visibles para un
 * usuario con scope desde el inicio del mes anterior, filtrados server-side por
 * las tiendas/clientes accesibles (en vez de bajar toda la colección y filtrar
 * en JS). Devuelve un array ordenado por fecha desc.
 *
 *  - administrador → null (usa getCountFromServer, no necesita esto).
 *  - sin scope efectivo → [].
 *
 * Requiere índices compuestos documents(tienda, fecha) y documents(cliente, fecha).
 */
export async function loadDashboardScopedDocs(adminData, currentService) {
  const serviceId = currentService?.id;
  if (!serviceId) return [];
  const scope = getUserScope(adminData);
  if (scope.isAll) return null;
  if (!hasScopeRestriction(scope)) return [];

  const since = startOfPrevMonth();
  const key = `${serviceId}|${adminData?.id || 'anon'}|${scopeSignature(adminData)}|${since.getTime()}`;
  const cached = scopedDocsCache.get(key);
  if (cached && Date.now() - cached.ts < SCOPED_DOCS_TTL_MS) return cached.promise;

  const promise = (async () => {
    const matchers = await loadVisibleDocumentMatchers(adminData, currentService);
    if (!matchers || matchers.isAll) return [];
    const tiendaValues = new Set();
    for (const v of (matchers.tiendas || [])) expandMatchValue(v, tiendaValues);
    const clienteValues = new Set();
    for (const v of (matchers.clientes || [])) expandMatchValue(v, clienteValues);
    if (tiendaValues.size === 0 && clienteValues.size === 0) return [];
    return fetchVisibleDocsSince(
      serviceId,
      since,
      Array.from(tiendaValues),
      Array.from(clienteValues),
    );
  })();

  scopedDocsCache.set(key, { ts: Date.now(), promise });
  // Si la descarga falla, no dejamos la promesa rechazada cacheada.
  promise.catch(() => scopedDocsCache.delete(key));
  return promise;
}
